import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import {
  GET_INSTRUCTOR_BY_ID,
  GET_CLASS_LIST,
  GET_INSTRUCTOR_UPDATES,
  GET_ATTENDANCE_BY_CLASS,
} from "../graphql/query";
import type { GetInstructorByIdResponse } from "../interfaces/instructor.interface";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "I";
}

function formatLabel(value?: string | null) {
  if (!value) return "—";
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionCard({ children, sx = {} }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E9EEF5",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid #F0F4F8",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#F0F4F8", color: "#6B7280" }}
        />
      )}
    </Box>
  );
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 2,
        borderRadius: "12px",
        bgcolor: "#FAFBFC",
        border: "1px solid #F0F4F8",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          bgcolor: "#EEF7E9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4CAF50",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontWeight: 600, color: "#111827", fontSize: 13, mt: 0.25, wordBreak: "break-word" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function OverviewSection({ instructor }: { instructor: any }) {
  const contactItems = [
    { icon: <MailOutlineIcon sx={{ fontSize: 18 }} />, label: "Email", value: instructor.email || "—" },
    { icon: <PhoneOutlinedIcon sx={{ fontSize: 18 }} />, label: "Contact Number", value: instructor.contactNumber || "—" },
    { icon: <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />, label: "Address", value: instructor.address || "—" },
    { icon: <InstagramIcon sx={{ fontSize: 18 }} />, label: "Instagram", value: instructor.instagramId || "—" },
  ];

  const professionalItems = [
    { icon: <WorkOutlineIcon sx={{ fontSize: 18 }} />, label: "Instructor Type", value: instructor.instructorType || "—" },
    { icon: <SchoolOutlinedIcon sx={{ fontSize: 18 }} />, label: "Class Style", value: instructor.classStyle || "—" },
    { icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />, label: "Type", value: formatLabel(instructor.classType) },
  ];

  return (
    <Stack spacing={2}>
      <SectionCard>
        <SectionHeader title="Contact Information" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {contactItems.map((item) => (
            <InfoItem key={item.label} icon={item.icon} label={item.label} value={item.value} />
          ))}
        </Box>
      </SectionCard>

      <SectionCard>
        <SectionHeader title="Professional Details" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
          {professionalItems.map((item) => (
            <InfoItem key={item.label} icon={item.icon} label={item.label} value={item.value} />
          ))}
        </Box>
        {instructor.about && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                bgcolor: "#FAFBFC",
                border: "1px solid #F0F4F8",
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  bgcolor: "#EEF7E9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#4CAF50",
                  flexShrink: 0,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}
                >
                  About
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 500, mt: 0.5, lineHeight: 1.7 }}>
                  {instructor.about}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader title="Account Details" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <InfoItem
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Member Since"
            value={formatDate(instructor.created_at)}
          />
          <InfoItem
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Last Updated"
            value={formatDate(instructor.updated_at)}
          />
        </Box>
      </SectionCard>

      {instructor.gallery && instructor.gallery.length > 0 && (
        <SectionCard>
          <SectionHeader title="Gallery" count={instructor.gallery.length} />
          <Box
            sx={{
              p: 2.5,
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            {instructor.gallery.map((img: string, idx: number) => (
              <Box
                key={idx}
                sx={{
                  aspectRatio: "1",
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid #E9EEF5",
                  bgcolor: "#F9FAFB",
                }}
              >
                <Box
                  component="img"
                  src={`data:image/jpeg;base64,${img}`}
                  alt={`Gallery ${idx + 1}`}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}
    </Stack>
  );
}

// ─── Classes ──────────────────────────────────────────────────────────────────

const CLASS_TYPE_COLORS: Record<string, string> = {
  REGULAR: "#4CAF50",
  WORKSHOP: "#1565C0",
  PRIVATE: "#7C3AED",
};

function SessionCard({ session, isUpcomingSession }: { session: any; isUpcomingSession: boolean }) {
  const sessionDateStr = session.sessionDate?.includes("T")
    ? session.sessionDate.split("T")[0]
    : session.sessionDate;

  const { data: attendanceData } = useQuery(GET_ATTENDANCE_BY_CLASS, {
    variables: { classId: session.id, date: sessionDateStr },
    skip: !session.id || isUpcomingSession,
    fetchPolicy: "network-only",
  });

  const attendance = attendanceData?.getAttendanceByClass?.data;
  const attendanceStudents: any[] = attendance?.students ?? [];
  const hasAttendance = attendanceStudents.length > 0;
  const enrolledStudents: string[] = session.enrolledStudents || [];
  const accentColor = CLASS_TYPE_COLORS[session.classType] || "#4CAF50";

  const studentRows = useMemo(() => {
    if (isUpcomingSession || !hasAttendance) {
      return enrolledStudents.map((name) => ({ name, status: "enrolled" as const }));
    }
    return attendanceStudents.map((s: any) => ({
      name: s.studentName,
      status: s.isPresent ? ("present" as const) : ("absent" as const),
    }));
  }, [isUpcomingSession, hasAttendance, enrolledStudents, attendanceStudents]);

  return (
    <Box sx={{ border: "1px solid #E9EEF5", borderRadius: "14px", overflow: "hidden", bgcolor: "#fff" }}>
      <Box sx={{ height: 4, bgcolor: accentColor }} />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }} noWrap>
              {session.className}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
              {formatLabel(session.classType)} · {formatDate(sessionDateStr)}
            </Typography>
          </Box>
          {session.isCancelled && (
            <Chip
              label="Cancelled"
              size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#FEF2F2", color: "#B91C1C", flexShrink: 0, ml: 1 }}
            />
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
            <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 600 }}>
              {session.startTime || "—"} – {session.endTime || "—"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <GroupOutlinedIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
            <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 600 }}>
              {enrolledStudents.length} students
            </Typography>
          </Box>
        </Stack>

        {hasAttendance && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 1.5,
              pb: 1.5,
              borderBottom: "1px solid #F0F4F8",
            }}
          >
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />}
              label={`Present: ${attendance.presentCount}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#E8F5E9", color: "#2E7D32", "& .MuiChip-icon": { color: "inherit" } }}
            />
            <Chip
              size="small"
              icon={<CancelOutlinedIcon sx={{ fontSize: "13px !important" }} />}
              label={`Absent: ${attendance.absentCount}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#FEF2F2", color: "#B91C1C", "& .MuiChip-icon": { color: "inherit" } }}
            />
          </Box>
        )}

        {studentRows.length === 0 ? (
          <Typography variant="caption" sx={{ color: "#D1D5DB", fontWeight: 600 }}>
            No students enrolled
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {studentRows.map((s) => (
              <Box
                key={s.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1,
                  borderRadius: "10px",
                  bgcolor: s.status === "present" ? "#F0FBF0" : s.status === "absent" ? "#FEF9F9" : "#FAFBFC",
                  border: `1px solid ${s.status === "present" ? "#D1FAE5" : s.status === "absent" ? "#FEE2E2" : "#F0F4F8"}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 28, height: 28, fontSize: 12, fontWeight: 700, flexShrink: 0,
                      bgcolor: s.status === "present" ? "#4CAF50" : s.status === "absent" ? "#E53935" : "#a3d65c",
                    }}
                  >
                    {s.name.trim()[0]?.toUpperCase() || "S"}
                  </Avatar>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }} noWrap>
                    {s.name}
                  </Typography>
                </Box>
                {s.status !== "enrolled" && (
                  <Chip
                    size="small"
                    label={s.status === "present" ? "Present" : "Absent"}
                    sx={{
                      height: 20, fontSize: 10, fontWeight: 700, flexShrink: 0,
                      bgcolor: s.status === "present" ? "#E8F5E9" : "#FEF2F2",
                      color: s.status === "present" ? "#2E7D32" : "#B91C1C",
                    }}
                  />
                )}
              </Box>
            ))}
          </Stack>
        )}

        {!isUpcomingSession && !hasAttendance && enrolledStudents.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #F0F4F8" }}>
            <Typography variant="caption" sx={{ color: "#D1D5DB", fontWeight: 600 }}>
              No attendance recorded
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ClassesSection({ classes }: { classes: any[] }) {
  const [sessionTab, setSessionTab] = useState(0);

  const { todaySessions, upcomingSessions, previousSessions } = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    const toDate = (d: string) => new Date(d.includes("T") ? d : `${d}T00:00:00`);

    const all: any[] = [];
    classes.forEach((cls: any) => {
      (cls.scheduledDates || []).forEach((dateStr: string) => {
        all.push({ ...cls, sessionDate: dateStr });
      });
    });

    const todayList = all.filter((s) => {
      const d = toDate(s.sessionDate);
      return d >= todayStart && d <= todayEnd;
    });
    const upcomingList = all
      .filter((s) => toDate(s.sessionDate) > todayEnd)
      .sort((a, b) => toDate(a.sessionDate).getTime() - toDate(b.sessionDate).getTime());
    const previousList = all
      .filter((s) => toDate(s.sessionDate) < todayStart)
      .sort((a, b) => toDate(b.sessionDate).getTime() - toDate(a.sessionDate).getTime());

    return { todaySessions: todayList, upcomingSessions: upcomingList, previousSessions: previousList };
  }, [classes]);

  if (classes.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <MenuBookOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No classes assigned yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  const TABS = [
    { label: "Today", sessions: todaySessions, isUpcoming: false },
    { label: "Upcoming", sessions: upcomingSessions, isUpcoming: true },
    { label: "Previous", sessions: previousSessions, isUpcoming: false },
  ];
  const currentTab = TABS[sessionTab];

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <Tabs
          value={sessionTab}
          onChange={(_, v) => setSessionTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              color: "#9CA3AF",
              minHeight: 44,
            },
            "& .Mui-selected": { color: "#111827 !important", fontWeight: 800 },
            "& .MuiTabs-indicator": { backgroundColor: "#a3d65c", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.label} label={`${t.label} (${t.sessions.length})`} />
          ))}
        </Tabs>
      </Box>

      {currentTab.sessions.length === 0 ? (
        <SectionCard>
          <Box sx={{ py: 6, textAlign: "center" }}>
            <MenuBookOutlinedIcon sx={{ fontSize: 36, color: "#D1D5DB", mb: 1 }} />
            <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600, display: "block" }}>
              No {currentTab.label.toLowerCase()} sessions
            </Typography>
          </Box>
        </SectionCard>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {currentTab.sessions.map((s: any, idx: number) => (
            <SessionCard
              key={`${s.id}-${s.sessionDate}-${idx}`}
              session={s}
              isUpcomingSession={currentTab.isUpcoming}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

// ─── Students ─────────────────────────────────────────────────────────────────

function ClassStudentsCard({ cls }: { cls: any }) {
  const { data: attendanceData } = useQuery(GET_ATTENDANCE_BY_CLASS, {
    variables: { classId: cls.id },
    skip: !cls.id,
    fetchPolicy: "network-only",
  });

  const attendance = attendanceData?.getAttendanceByClass?.data;
  const enrolledStudents: string[] = cls.enrolledStudents || [];
  const hasAttendance = (attendance?.students?.length ?? 0) > 0;

  const attendanceMap = useMemo(() => {
    const map = new Map<string, boolean>();
    (attendance?.students || []).forEach((s: any) => map.set(s.studentName, s.isPresent));
    return map;
  }, [attendance]);

  const CLASS_TYPE_COLORS: Record<string, string> = {
    REGULAR: "#4CAF50",
    WORKSHOP: "#1565C0",
    PRIVATE: "#7C3AED",
  };
  const accentColor = CLASS_TYPE_COLORS[cls.classType] || "#4CAF50";

  return (
    <SectionCard>
      <Box sx={{ height: 4, bgcolor: accentColor }} />
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #F0F4F8",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
            {cls.className}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
            {formatLabel(cls.classType)} · {cls.day || "—"} · {cls.startTime || "—"} – {cls.endTime || "—"}
          </Typography>
        </Box>
        <Chip
          label={`${enrolledStudents.length} enrolled`}
          size="small"
          sx={{ height: 22, fontWeight: 700, fontSize: 11, bgcolor: "#F0F4F8", color: "#6B7280" }}
        />
      </Box>

      {enrolledStudents.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <GroupOutlinedIcon sx={{ fontSize: 36, color: "#D1D5DB", mb: 1 }} />
          <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600, display: "block" }}>
            No students enrolled
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            gap: 1.5,
          }}
        >
          {enrolledStudents.map((name: string) => {
            const hasStatus = attendanceMap.has(name);
            const isPresent = attendanceMap.get(name);
            return (
              <Box
                key={name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  p: 1.5,
                  borderRadius: "10px",
                  bgcolor: "#FAFBFC",
                  border: "1px solid #F0F4F8",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <Avatar
                    sx={{ width: 32, height: 32, bgcolor: "#a3d65c", fontSize: 13, fontWeight: 700, flexShrink: 0 }}
                  >
                    {name.trim()[0]?.toUpperCase() || "S"}
                  </Avatar>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }} noWrap>
                    {name}
                  </Typography>
                </Box>
                {hasStatus && (
                  <Chip
                    size="small"
                    label={isPresent ? "Present" : "Absent"}
                    sx={{
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      flexShrink: 0,
                      bgcolor: isPresent ? "#E8F5E9" : "#FEF2F2",
                      color: isPresent ? "#2E7D32" : "#B91C1C",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {hasAttendance && (
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #F0F4F8",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700 }}>
            Last Attendance:
          </Typography>
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />}
            label={`Present: ${attendance.presentCount}`}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#E8F5E9", color: "#2E7D32", "& .MuiChip-icon": { color: "inherit" } }}
          />
          <Chip
            size="small"
            icon={<CancelOutlinedIcon sx={{ fontSize: "13px !important" }} />}
            label={`Absent: ${attendance.absentCount}`}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#FEF2F2", color: "#B91C1C", "& .MuiChip-icon": { color: "inherit" } }}
          />
          <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600, ml: "auto" }}>
            {formatDate(attendance.date)}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
}

function StudentsSection({ studentsCount, classes }: { studentsCount: number; classes: any[] }) {
  return (
    <Stack spacing={2}>
      <Box
        sx={{
          bgcolor: "#F0FBF0",
          border: "1px solid #E8F5E9",
          borderRadius: "16px",
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 56, fontWeight: 900, color: "#4CAF50", lineHeight: 1 }}>
          {studentsCount}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          Total Students
        </Typography>
      </Box>

      {classes.map((cls: any) => (
        <ClassStudentsCard key={cls.id} cls={cls} />
      ))}
    </Stack>
  );
}

// ─── Activity ─────────────────────────────────────────────────────────────────

const ACTIVITY_PAGE_SIZE = 10;

function ActivitySection({ updates }: { updates: any[] }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(updates.length / ACTIVITY_PAGE_SIZE);
  const pagedUpdates = updates.slice(page * ACTIVITY_PAGE_SIZE, (page + 1) * ACTIVITY_PAGE_SIZE);

  if (updates.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <ShowChartIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No activity recorded yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  const getConfig = (type: string) => {
    if (type === "CLASS_CANCELLATION")
      return { bg: "#FEF2F2", color: "#B91C1C", icon: <CancelOutlinedIcon sx={{ fontSize: 18 }} /> };
    if (type === "NOTIFICATION")
      return { bg: "#EBF2FF", color: "#1565C0", icon: <NotificationsNoneIcon sx={{ fontSize: 18 }} /> };
    return { bg: "#EEF7E9", color: "#2E7D32", icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> };
  };

  return (
    <SectionCard>
      <SectionHeader title="Activity Timeline" count={updates.length} />
      <Box sx={{ px: 3, py: 2.5 }}>
        {pagedUpdates.map((upd, idx) => {
          const cfg = getConfig(upd.type);
          return (
            <Box key={upd.id || idx} sx={{ display: "flex", gap: 2, position: "relative" }}>
              {idx < pagedUpdates.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 17,
                    top: 38,
                    bottom: -10,
                    width: 2,
                    bgcolor: "#F0F4F8",
                    zIndex: 0,
                  }}
                />
              )}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: cfg.bg,
                  color: cfg.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  zIndex: 1,
                  border: `2px solid ${cfg.color}22`,
                }}
              >
                {cfg.icon}
              </Box>
              <Box sx={{ flex: 1, pb: idx < pagedUpdates.length - 1 ? 2.5 : 0, pt: 0.25 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: cfg.color, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.5px" }}
                    >
                      {(upd.type || "").replace(/_/g, " ")}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: 13.5, lineHeight: 1.3 }}>
                      {upd.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                      {upd.message}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#9CA3AF", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {formatDate(upd.time)}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Box>
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F0F4F8" }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, v) => setPage(v - 1)}
            size="small"
            sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#a3d65c !important", color: "#fff" } }}
          />
        </Box>
      )}
    </SectionCard>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { label: "Overview", icon: <PersonOutlineIcon sx={{ fontSize: 16 }} /> },
  { label: "Classes", icon: <MenuBookOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Students", icon: <GroupOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Activity", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
];

const InstructorReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { data: instructorData, loading: instructorLoading } =
    useQuery<GetInstructorByIdResponse>(GET_INSTRUCTOR_BY_ID, {
      variables: { id },
      skip: !id,
      fetchPolicy: "network-only",
    });

  const { data: classesData, loading: classesLoading } =
    useQuery(GET_CLASS_LIST, {
      variables: { page: 1, perPage: 100, instructorId: id },
      skip: !id,
      fetchPolicy: "network-only",
    });

  const { data: updatesData, loading: updatesLoading } =
    useQuery(GET_INSTRUCTOR_UPDATES, {
      variables: { instructorId: id },
      skip: !id,
      fetchPolicy: "network-only",
    });

  const isLoading = instructorLoading || classesLoading || updatesLoading;
  const instructor = instructorData?.getInstructor?.data;
  const classes = classesData?.getClassList?.data ?? [];
  const updates = updatesData?.getInstructorUpdates?.updates ?? [];

  const uniqueStudentNames = useMemo(() => {
    const set = new Set<string>();
    classes.forEach((c: any) => (c.enrolledStudents || []).forEach((s: string) => set.add(s)));
    return Array.from(set).sort();
  }, [classes]);

  const quickStats = useMemo(
    () => ({
      classes: classes.length,
      students: uniqueStudentNames.length,
      classType: instructor?.classType || "—",
    }),
    [classes, instructor, uniqueStudentNames],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={36} sx={{ color: "#a3d65c" }} />
      </Box>
    );
  }

  if (!instructor) {
    return (
      <Box sx={{ mt: 8, textAlign: "center" }}>
        <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>Instructor not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 }, px: { xs: 1, md: 3 }, pb: 6 }}>
      {/* Back link */}
      <Box
        onClick={() => navigate("/instructors")}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          color: "#6B7280",
          fontSize: 13,
          fontWeight: 600,
          mb: 2,
          "&:hover": { color: "#111827" },
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
        Back to Instructors
      </Box>

      {/* ── Profile Hero ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "20px",
          overflow: "hidden",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2.5} alignItems={{ sm: "center" }}>
            {/* Avatar */}
            <Avatar
              src={instructor.profileImage ? `data:image/jpeg;base64,${instructor.profileImage}` : undefined}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#a3d65c",
                color: "#fff",
                fontWeight: 900,
                fontSize: 26,
                flexShrink: 0,
                boxShadow: "0 4px 14px rgba(163,214,92,0.4)",
              }}
            >
              {initials(instructor.fullname || "")}
            </Avatar>

            {/* Name block */}
            <Box flex={1}>
              <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#111827", lineHeight: 1.2 }}>
                  {instructor.fullname}
                </Typography>
                <Chip
                  label={instructor.isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    fontSize: 11,
                    bgcolor: instructor.isActive ? "#E8F5E9" : "#FEF2F2",
                    color: instructor.isActive ? "#2E7D32" : "#B91C1C",
                  }}
                />
                {instructor.instructorType && (
                  <Chip
                    label={instructor.instructorType}
                    size="small"
                    sx={{ height: 24, fontWeight: 800, fontSize: 11, bgcolor: "#EBF2FF", color: "#1565C0" }}
                  />
                )}
              </Stack>
              <Typography sx={{ color: "#9CA3AF", fontWeight: 600, fontSize: 13, mt: 0.25 }}>
                {instructor.instructorId} · Member since {formatDate(instructor.created_at)}
              </Typography>
            </Box>
          </Stack>

          {/* Quick stats strip */}
          <Box
            sx={{
              mt: 3,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.5,
            }}
          >
            {[
              { label: "Total Classes", value: quickStats.classes, color: "#1565C0", bg: "#EBF2FF", isText: false },
              { label: "Total Students", value: quickStats.students, color: "#4CAF50", bg: "#E8F5E9", isText: false },
              { label: "Type", value: formatLabel(quickStats.classType), color: "#7C3AED", bg: "#F5F0FF", isText: true },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  textAlign: "center",
                  py: 1.75,
                  px: 1,
                  borderRadius: "12px",
                  bgcolor: s.bg,
                  border: `1px solid ${s.color}22`,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: s.isText ? 13 : 24,
                    color: s.color,
                    lineHeight: s.isText ? 1.3 : 1,
                    wordBreak: "break-word",
                    textDecoration: "none",
                  }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", fontSize: 11 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "14px",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13.5,
              color: "#9CA3AF",
              minHeight: 48,
              gap: 0.75,
            },
            "& .Mui-selected": { color: "#111827 !important", fontWeight: 800 },
            "& .MuiTabs-indicator": {
              backgroundColor: "#a3d65c",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          {TABS.map((t, i) => {
            const counts = [undefined, classes.length, uniqueStudentNames.length, updates.length];
            const count = counts[i];
            return (
              <Tab key={t.label} label={count !== undefined ? `${t.label} (${count})` : t.label} icon={t.icon} iconPosition="start" />
            );
          })}
        </Tabs>
      </Box>

      {/* ── Tab content ── */}
      {activeTab === 0 && <OverviewSection instructor={instructor} />}
      {activeTab === 1 && <ClassesSection classes={classes} />}
      {activeTab === 2 && <StudentsSection studentsCount={quickStats.students} classes={classes} />}
      {activeTab === 3 && <ActivitySection updates={updates} />}
    </Box>
  );
};

export default InstructorReport;
